import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useEffect } from "react";
import { ThemeProvider } from "@/components/theme-provider";
import ErrorBoundary from "@/components/ErrorBoundary";
import Index from "./pages/Index";
import Products from "./pages/Products";
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
import Register from "./pages/Register";
import SellerRegister from "./pages/SellerRegister";
import SellerLogin from "./pages/SellerLogin";
import SellerOnboarding from "./pages/SellerOnboarding";
import SellerDashboard from "./pages/SellerDashboard";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import ProductDetail from "./pages/ProductDetail";
import Category from "./pages/Category";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SellerShop from "./pages/SellerShop";
import SellerProducts from "./pages/SellerProducts";
import SellerProductNew from "./pages/SellerProductNew";
import SellerProductEdit from "./pages/SellerProductEdit";
import RequireAuth from "@/components/RequireAuth";
import OrderDetail from "./pages/OrderDetail";
import Orders from "./pages/Orders";
import ShopPublic from "./pages/ShopPublic";
import SellerModeration from "./pages/SellerModeration";
import Analytics from "./pages/Analytics";
import AuthCallback from "./pages/AuthCallback";
import UserSettings from "./pages/UserSettings";
import UserProfile from "./pages/UserProfile";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import TestAuth from "./pages/TestAuth";
import { createEnhancedQueryClient, cacheUtils, backgroundSync } from "@/lib/cache";
import CacheManager from "@/components/CacheManager";

const queryClient = createEnhancedQueryClient();

function App() {
  useEffect(() => {
    try {
      // Warm cache with critical data on app startup
      cacheUtils.warmCache(queryClient);

      // Start background sync
      backgroundSync.startSync(queryClient);

      // Log cache performance stats in development
      if (import.meta.env.DEV) {
        setTimeout(() => {
          try {
            const stats = cacheUtils.getCacheStats(queryClient);
            console.log('📊 Cache Stats:', stats);
          } catch (error) {
            console.warn('Cache stats not available:', error);
          }
        }, 2000);
      }
    } catch (error) {
      console.warn('Cache initialization failed (non-critical):', error);
    }
  }, []);
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider defaultTheme="light" storageKey="bahamarket-theme">
            <TooltipProvider>
              <BrowserRouter>
                <div className="min-h-screen flex flex-col">
                  <Header />
                  <main className="flex-1">
                    <Routes>
                      {/* Main Routes */}
                      <Route path="/" element={<Index />} />
                      <Route path="/products" element={<Products />} />

                      {/* Customer Authentication */}
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/test-auth" element={<TestAuth />} />
                      <Route path="/auth/reset-password" element={<ResetPassword />} />

                      {/* Seller Authentication & Onboarding */}
                      <Route path="/seller/register" element={<SellerRegister />} />
                      <Route path="/seller/login" element={<SellerLogin />} />
                      <Route path="/seller/onboarding" element={<RequireAuth role="seller"><SellerOnboarding /></RequireAuth>} />

                      {/* User Profile & Settings */}
                      <Route path="/settings" element={<UserSettings />} />
                      <Route path="/profile" element={<UserProfile />} />

                      {/* Seller Dashboard & Management */}
                      <Route path="/seller" element={<RequireAuth role="seller"><SellerDashboard /></RequireAuth>} />
                      <Route path="/seller/shop" element={<RequireAuth role="seller"><SellerShop /></RequireAuth>} />
                      <Route path="/seller/products" element={<RequireAuth role="seller"><SellerProducts /></RequireAuth>} />
                      <Route path="/seller/products/new" element={<RequireAuth role="seller"><SellerProductNew /></RequireAuth>} />
                      <Route path="/seller/products/:id/edit" element={<RequireAuth role="seller"><SellerProductEdit /></RequireAuth>} />
                      <Route path="/seller/moderation" element={<RequireAuth role="seller"><SellerModeration /></RequireAuth>} />
                      <Route path="/seller/analytics" element={<RequireAuth role="seller"><Analytics /></RequireAuth>} />

                      {/* Shopping & Orders */}
                      <Route path="/cart" element={<Cart />} />
                      <Route path="/checkout" element={<Checkout />} />
                      <Route path="/products/:id" element={<ProductDetail />} />
                      <Route path="/category/:slug" element={<Category />} />
                      <Route path="/orders" element={<Orders />} />
                      <Route path="/orders/:id" element={<OrderDetail />} />

                      {/* Public Shop Pages */}
                      <Route path="/shop/:handle" element={<ShopPublic />} />

                      {/* 404 */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </main>
                  <Footer />
                </div>
                <Toaster />
                <CacheManager />
              </BrowserRouter>
            </TooltipProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;